import React, { useEffect, useMemo, useState } from 'react';
import { prop, pick } from 'ramda';
import { FormSpy } from 'react-final-form';
import Form from 'js/components/common/Form';
import { Loader } from 'js/components/common/Loader';
import { clsb, clsx } from 'js/utils/classNames.util';
import { ReCaptcha } from 'js/components/main/ReCaptcha';
import { useComponentTranslations } from 'js/hooks/component';
import { ErrorMessage } from 'js/components/common/ErrorMessage';
import { FormErrorMessage } from 'js/components/common/FormFields';
import { useAppSelector, useFormsSelector, useUserSelector } from 'js/store/hooks';

import * as hooks from './hooks';
import * as actions from './store/actions';
import { getStepColumns } from './helpers';
import { MONEY_FIELDS } from './constants';
import { Controls } from './components/Controls';
import { FormRulesSpy } from './components/FormRulesSpy';
import { useLimitsSettings } from './components/Limits/hooks';
import { ChangeFormField } from './components/ChangeFormField';
import { useDepositLimitsSettings } from './components/DepositLimits/hooks';
import { useRecaptchaValidation } from './components/ChangeFormField/hooks';
import { withDynamicReducer, useDynamicReducerSelector, useDynamicReducerDispatch } from './store';

import style from './ChangeForm.scss';

export const ChangeFormBare = ({
    /** @type Object */
    formConfig: customFormConfig = null,
    formName,
    onSubmit,
    /** @type Object */
    startingValues = null,
    formError = '',
    validationOperation = '',
    submitTags = true,
    /** @type Object */
    classNames = {}
}) => {
    const translate = useComponentTranslations();
    const dispatch = useDynamicReducerDispatch();
    const {
        isLoading, fields, formInternalError, hiddenByRulesFields, rules, steps
    } = useDynamicReducerSelector(pick([
        'isLoading', 'fields', 'formInternalError', 'hiddenByRulesFields', 'rules', 'steps'
    ]));
    const defaultCurrency = useAppSelector(prop('defaultCurrency'));
    const { currencyCode, userDetails } = useUserSelector(pick(['currencyCode', 'userDetails']));
    const isFormsLoaded = useFormsSelector(prop('isFormsLoaded'));
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [valuesAfterSubmit, setValuesAfterSubmit] = useState({});

    const formConfig = hooks.useForm({ formName, customFormConfig });
    const [playerTags, fetchPlayerTags] = hooks.usePlayerTags();
    const getUserValues = hooks.useGetUserValues({ playerTags });

    const userValues = getUserValues({ fields, userDetails });
    const defaultValues = hooks.useDefaultValues();
    const urlSearchValues = hooks.useURLSearchValues();
    const updateImsValues = hooks.useUpdateImsValues();
    const buildCls = clsb([style, classNames]);

    const combinedInitialValues = useMemo(() => ({
        ...startingValues,
        ...defaultValues,
        ...urlSearchValues,
        ...userValues,
        ...valuesAfterSubmit
    }), [startingValues, defaultValues, userValues, urlSearchValues, valuesAfterSubmit]);

    hooks.useFieldsAndRules({ formConfig, formName });

    const validationConfig = hooks.useValidationConfig();
    const {
        isReCaptchaVisible,
        onReCaptchaTokenValidate,
        onReCaptchaTokenError,
        requestReCaptchaToken,
        abortReCaptchaTokenRequest
    } = useRecaptchaValidation();

    const onCombinedSubmit = hooks.useOnCombinedSubmit({
        onSubmit,
        submitTags,
        playerTags,
        fetchPlayerTags,
        setValuesAfterSubmit,
        requestReCaptchaToken,
        initialValues: userValues,
        abortReCaptchaTokenRequest
    });

    useEffect(() => {
        dispatch(actions.setLimitsCurrency(currencyCode || defaultCurrency));
    }, [dispatch, currencyCode, defaultCurrency]);

    useDepositLimitsSettings();
    useLimitsSettings();

    if (!isFormsLoaded && !customFormConfig) {
        return <Loader />;
    }

    return (
        <Form
            key={formName}
            onSubmit={onCombinedSubmit}
            fieldsToReplaceComma={MONEY_FIELDS}
            validationConfig={validationConfig}
            initialValues={combinedInitialValues}
            render={({ handleSubmit, submitting, submitError }) => (
                <form
                    onSubmit={event => event.preventDefault()}
                    data-form-name={formName}
                    className={`${formConfig ? buildCls(['form']) : null} cms-dynamic-form cms-${formName}`}
                >
                    {formConfig ? null : (
                        <ErrorMessage
                            className={buildCls(['error'])}
                            error={translate('playtech.mobile.system.change-form.error.incorrect-form', [formName])}
                        />)
                    }
                    {(submitError || formError || formInternalError) && (
                        <FormErrorMessage
                            className={buildCls(['formError'])}
                            message={submitError || formError || formInternalError}
                        />
                    )}
                    {rules.length > 0 && (
                        <FormSpy
                            subscription={{ active: true, values: true }}
                            render={({ active, values }) => (<FormRulesSpy active={active} values={values} />)}
                        />
                    )}
                    <FormSpy onChange={updateImsValues} subscription={{ values: true }} />
                    {steps.map((step, index) => {
                        const columns = getStepColumns(step);

                        return (
                            <fieldset key={index} className={clsx([`cms-form-step-${index + 1}`, index !== currentStepIndex && 'hidden'])}>
                                <div className={style.columns}>
                                    {Object.values(columns).map((column, i) => (
                                        <div className={`${style.column} cms-form-column-${i + 1}`} key={i}>
                                            {column.map((field) => {
                                                const isFieldHidden = hiddenByRulesFields.includes(field.name);

                                                return (
                                                    <ChangeFormField
                                                        field={field}
                                                        key={field.name}
                                                        handleSubmit={handleSubmit}
                                                        isFieldHidden={isFieldHidden}
                                                        disabled={submitting || isLoading}
                                                    />
                                                );
                                            })}
                                        </div>
                                    ))}
                                </div>
                            </fieldset>
                        );
                    })}
                    {isReCaptchaVisible && (
                        <div className={buildCls(['captcha'])}>
                            <ReCaptcha
                                operation={validationOperation}
                                onValidate={onReCaptchaTokenValidate}
                                onError={onReCaptchaTokenError}
                            />
                        </div>
                    )}
                    {formConfig && (
                        <Controls
                            handleSubmit={handleSubmit}
                            currentStep={currentStepIndex + 1}
                            disabled={submitting || isLoading}
                            setCurrentStepIndex={setCurrentStepIndex}
                        />
                    )}
                </form>
            )}
        />
    );
};

export const ChangeForm = withDynamicReducer()(ChangeFormBare);
